import Image from "next/image";
import UpIcon from "./images/up.svg";
import DownIcon from "./images/down.svg";
import { useState, useRef, useEffect } from "react";
import { Icon as IconType } from "@/types/icon";
import Icon from "@/components/common/Icon";
import { generateLicenseLink } from "@/utils/links";

interface Props {
  icons: IconType[];
  linkTarget: string;
  multipleIcon?: IconType;
  licenseKeys: string[];
  locale: string;
  slug: string;
}

export default function MultiLicenseCell({
  icons,
  linkTarget,
  multipleIcon,
  licenseKeys,
  locale,
  slug,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (isOpen) {
      listRef.current?.focus();
    }
  }, [isOpen]);

  // Only use icon if provided from i18n API
  if (!multipleIcon) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  return (
    <div
      className="dropdown-container w-full min-h-[44px] min-w-[44px]"
      onKeyDown={handleKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        className="clickable-icon-cell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={
          [multipleIcon.name, multipleIcon.description?.trim()]
            .filter(Boolean)
            .join(" – ") || "Multiple licenses"
        }
        aria-expanded={isOpen}
      >
        <div className="flex flex-col items-center">
          <Icon icon={multipleIcon} width={70} height={60} alt="" />
          <span aria-hidden="true" className="text-sm">
            {multipleIcon?.name}
          </span>
        </div>
        <Image
          className="dropdown-icon"
          src={isOpen ? UpIcon : DownIcon}
          alt=""
          width={20}
          height={20}
        />
      </button>
      {isOpen && (
        <ul className="dropdown-content" ref={listRef} tabIndex={-1}>
          {icons.map((icon, index) => {
            const licenseKey = licenseKeys[index];
            const finalUrl = generateLicenseLink(licenseKey, locale, slug);
            return (
              <li key={licenseKey}>
                <a
                  className="tooltip"
                  href={finalUrl}
                  target={finalUrl !== "#" ? linkTarget : undefined}
                  aria-label={[icon.name, icon.description?.trim()]
                    .filter(Boolean)
                    .join(" – ")}
                >
                  <Icon
                    icon={icon}
                    width={110}
                    height={60}
                    showArrow={finalUrl !== "#"}
                    alt={[icon.name, icon.description?.trim()]
                      .filter(Boolean)
                      .join(" – ")}
                  />
                  <span className="tooltiptext" aria-hidden="true">
                    {icon.description?.trim()}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
